import { CommonModule, NgFor, NgIf } from '@angular/common'
import { Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core'
import { CapitalizePipe } from '../../../pipes/capitalize.pipe'
import { PaginationComponent } from '../../template/pagination/pagination.component'
import { RouterModule } from '@angular/router'
import { NgSelectModule } from '@ng-select/ng-select'
import { Store } from '@ngrx/store'
import {
  BehaviorSubject,
  Observable,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs'
import { LeavePolicyService } from '../../../services/leave-policy.service'
import { ToastrService } from 'ngx-toastr'
import { LoaderComponent } from '../../../shared/components/loader/loader.component'
import { environment } from '../../../../environments/environment'
import { eps } from '../../../services/url.service'
import { AuthState, selectUser, UserBusinesses } from '../../../store/auth/auth.state'
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { LeaveType } from '../../../store/leave-policy/leave-policy.state'
import { HasPermissionDirective } from '../../../directives/has-permission.directive'
import { SharedModule } from '../../../shared/shared.module'
import { selectAllBusinesses } from '../../../store/business/business.selectors'
import { Business } from '../../../store/business/business.state'
import { loadBusinesses } from '../../../store/business/business.actions'

@Component({
  selector: 'app-leave-balance',
  imports: [
    NgFor,
    NgIf,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    CapitalizePipe,
    PaginationComponent,
    RouterModule,
    NgSelectModule,
    LoaderComponent,
    HasPermissionDirective,
    SharedModule
  ],
  templateUrl: './leave-balance.component.html',
  styleUrl: './leave-balance.component.scss'
})
export class LeaveBalanceComponent implements OnInit, OnDestroy {
  private store = inject(Store)
  private modalService = inject(NgbModal)
  private fb = inject(FormBuilder)
  toastr = inject(ToastrService)
  destroy$ = new Subject<void>()
  apiEndpoint = environment.apiBaseUrl
  eps = eps

  // --- State Properties ---
  totalCount = 0
  currentPage = 1
  pageLimit = 10
  search = ''
  order = 'desc'
  assignedPolicyBalanceList: any[] = []
  selectedBusiness = ''
  businesses: UserBusinesses[] = []
  isEmployeeActive = true
  selectedUser = ''
  policyId = ''
  pageLastRecord = 0
  user: AuthState['user'] | null = null
  form!: FormGroup
  editAssignment!: any
  public selectedLeave: any = ''
  parsedDateRange: { date: string; halfDayOption: string; selectionType: string }[] = []

  // --- Offcanvas & Other UI Properties ---
  @ViewChild('editModal') editModal!: TemplateRef<any>
  selectedBalances$ = new BehaviorSubject<Record<string, any[]>>({})
  offcanvasTitle = ''
  showNextAccrualDetails = false
  userAndPolicyName = ''
  policyType = ''
  private fetchTrigger$ = new Subject<void>()
  leaveTypeEnum = LeaveType
  isBusinessOwner: boolean | undefined
  isAdmin: boolean | undefined
  businessId: string | null = null
  businesses$: Observable<Business[]> = this.store.select(selectAllBusinesses)

  constructor(public leavePolicyService: LeavePolicyService) {
    this.store
      .select(selectUser)
      .pipe(
        take(1),
        tap(user => {
          this.user = user
          // if (user?.role.name === 'Employee') {
          if (user?.userRoles?.some(ur => ur.role?.name === 'Employee')) {
            this.selectedUser = user.id
          }
          this.isAdmin = user?.userRoles?.some(ur => ur.role?.name === 'Admin')
          this.isBusinessOwner = user?.userRoles?.some(ur => ur.role?.name === 'Business Owner')
          this.selectedBusiness = user?.businessId ?? ''
        })
      )
      .subscribe()

    this.store.dispatch(loadBusinesses({ params: null }))
  }

  ngOnInit(): void {
    this.fetchTrigger$
      .pipe(
        startWith(null),
        takeUntil(this.destroy$),
        switchMap(() => {
          const safeBusinessId = this.selectedBusiness ? this.selectedBusiness : ''
          const safePolicyId = this.policyId ? this.policyId : ''

          return this.leavePolicyService.assignedPolicyList(
            safeBusinessId,
            safePolicyId,
            this.currentPage,
            this.pageLimit,
            this.isEmployeeActive,
            this.search,
            this.selectedUser
          )
        })
      )
      .subscribe({
        next: (response: any) => {
          this.totalCount = response.totalCount
          this.assignedPolicyBalanceList = response.assignedLeavePolicies
          this.pageLastRecord = Math.min(this.currentPage * this.pageLimit, this.totalCount)
        },
        error: (error: { message: string }) => {
          this.toastr.error('Error: ' + error.message)
        }
      })
    this.getEmpAndPolicyName()
    this.form = this.fb.group({
      balance: [null, [Validators.required]],
      reason: ['']
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  onFilterChange(resetPage = false) {
    if (resetPage) this.currentPage = 1
    this.fetchTrigger$.next()
  }

  onPageChange(page: number) {
    this.currentPage = page
    this.onFilterChange()
  }

  getErrorMessage(controlName: string): string | null {
    const control = this.form.get(controlName)
    if (control && control.touched && control.invalid) {
      if (control.hasError('required')) {
        return 'This field is required'
      }
    }
    return null
  }

  openEditModal(modal: unknown, assignment: any) {
    this.editAssignment = assignment
    this.modalService.open(modal, {
      scrollable: false,
      centered: true,
      size: 'md'
    })
  }

  onClose() {
    this.showNextAccrualDetails = !this.showNextAccrualDetails
    this.modalService.dismissAll()
    this.form.reset()
  }

  onUpdateBalance() {
    if (this.form.invalid || !this.editAssignment) return

    const { balance, reason } = this.form.value

    const payload = {
      userId: this.editAssignment.user.id,
      leavePolicyId: this.editAssignment.leavePolicy.id,
      balance: balance,
      reason: reason
    }

    this.leavePolicyService.updateLeaveBalance(payload).subscribe({
      next: () => {
        this.toastr.success('Leave balance updated successfully!')
        this.onClose()
      },
      error: err => {
        this.toastr.error('Failed to update leave balance')
      }
    })
  }

  getInitials(firstName = '', lastName = ''): string {
    const firstInitial = firstName?.charAt(0) || ''
    const lastInitial = lastName?.charAt(0) || ''
    return (firstInitial + lastInitial).toUpperCase()
  }

  private getEmpAndPolicyName() {
    this.selectedBalances$.pipe(takeUntil(this.destroy$)).subscribe(data => {
      const user = Object.entries(data)?.[0]?.[1]?.[0]?.user
      const policy = Object.entries(data)?.[0]?.[1]?.[0]?.leavePolicy
      this.userAndPolicyName = `${user?.firstName} ${user?.lastName} | ${policy?.title}`
      this.policyType = Object.entries(data)?.[0]?.[1]?.[0]?.leavePolicy.leaveType
    })
  }

  toggleAccrualDetails(): void {
    this.showNextAccrualDetails = !this.showNextAccrualDetails
  }

  totalBalance(currentBalance: number, upcomingBalance: number): number {
    const t = Number(currentBalance) + Number(upcomingBalance)
    return Number(t.toFixed(2))
  }

  sortYearsDesc = (a: any, b: any): number => {
    return a.key > b.key ? -1 : 1
  }

  balanceViewerDrawer(userId: string, leavePolicyId: string, assignedLeavePolicyId: string) {
    this.userAndPolicyName = ''
    this.policyType = ''
    if (userId) {
      const paramsObj = {
        userId: userId,
        leavePolicyId: leavePolicyId,
        assignedLeavePolicyId
      }

      const params = new URLSearchParams(paramsObj).toString()
      this.leavePolicyService.getAccrual(params).subscribe(newBalances => {
        // You can still push to it
        this.selectedBalances$.next(newBalances)
      })
    }
  }

  openOffcanvasLeave(leave: any) {
    this.selectedLeave = leave
    if (this.selectedLeave?.appliedLeaves?.dateRangeforHalfDay) {
      try {
        this.parsedDateRange = JSON.parse(this.selectedLeave.appliedLeaves.dateRangeforHalfDay)
        // console.log('date range', this.parsedDateRange)
      } catch (error) {
        console.error('Invalid JSON in dateRangeforHalfDay:', error)
        this.parsedDateRange = []
      }
    }
  }

  getFilteredBalances(values: any[]) {
    if (!values || values.length === 0) return []

    // If last has upcomingBalance = 0, remove it
    if (values[values.length - 1].upcomingBalance === 0) {
      return values.slice(0, -1)
    }
    return values
  }
  customSearchFn = (term: string, item: any) => {
    const fullName = `${item.businessName}`.toLowerCase()
    return fullName.includes(term.toLowerCase())
  }

  isDisabled(data: { [year: string]: any[] }): boolean {
    const firstYear = Object.keys(data)[0]
    const balance = firstYear ? data[firstYear]?.[0] : null

    return balance?.disabled === true
  }
}
